// BILLING-PLANS: the small parts the client's Plan area and Operator › Billing share — labels,
// status chips, an outbound row link (Pay ↗, PDF ↗) and a section heading.
import { Badge, InfoTip } from "@companyos/ui";
import type { ComponentType, ReactNode } from "react";
import { formatDate } from "../../format";
import { RowLinkAction } from "../../ops-ux";
import type {
  InvoiceDisplayStatus,
  InvoiceView,
  MaintenanceTier,
  Quote,
  SubscriptionView,
} from "../plan-queries";

export const TIER_LABEL: Record<MaintenanceTier, string> = {
  standard: "Standard",
  advanced: "Advanced",
  managed: "Managed",
};

export const SOURCE_LABEL: Record<InvoiceView["source"], string> = {
  subscription: "Plan",
  topup: "Credit",
  setup: "Setup",
  milestone: "Milestone",
  maintenance: "Maintenance",
  one_off: "One-off",
};

const INVOICE_STATUS: Record<
  InvoiceDisplayStatus,
  { label: string; tone: "neutral" | "info" | "success" | "warning" | "danger" }
> = {
  draft: { label: "Draft", tone: "neutral" },
  open: { label: "Open", tone: "info" },
  overdue: { label: "Overdue", tone: "danger" },
  paid: { label: "Paid", tone: "success" },
  void: { label: "Void", tone: "neutral" },
  uncollectible: { label: "Uncollectible", tone: "warning" },
};

export function InvoiceStatusChip({ invoice }: { invoice: InvoiceView }) {
  const s = INVOICE_STATUS[invoice.display_status];
  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant={s.tone}>{s.label}</Badge>
      {invoice.display_status === "overdue" && invoice.grace_until ? (
        <InfoTip label={`About overdue invoice ${invoice.number ?? ""}`.trim()}>
          Unpaid past its due date. Pay by {formatDate(invoice.grace_until)} to keep the plan
          running.
        </InfoTip>
      ) : null}
    </span>
  );
}

const QUOTE_STATUS: Record<
  Quote["status"],
  { label: string; tone: "neutral" | "info" | "success" | "warning" | "danger" }
> = {
  draft: { label: "Draft", tone: "neutral" },
  open: { label: "Open", tone: "info" },
  accepted: { label: "Accepted", tone: "success" },
  canceled: { label: "Cancelled", tone: "neutral" },
  expired: { label: "Expired", tone: "warning" },
};

export function QuoteStatusChip({ status }: { status: Quote["status"] }) {
  const s = QUOTE_STATUS[status];
  return <Badge variant={s.tone}>{s.label}</Badge>;
}

/** The subscription's status chip, with the grace date behind an (i) when it matters. */
export function SubscriptionStatusChip({ sub }: { sub: SubscriptionView }) {
  const chip = (() => {
    switch (sub.status) {
      case "trialing":
        return {
          label: sub.trial_end ? `Trial until ${formatDate(sub.trial_end)}` : "Trial",
          tone: "info" as const,
          tip: "The plan is free until the trial ends; the first invoice follows.",
        };
      case "active":
        return sub.cancel_at_period_end && sub.current_period_end
          ? {
              label: `Ends ${formatDate(sub.current_period_end)}`,
              tone: "warning" as const,
              tip: "The plan stops at the end of this period.",
            }
          : { label: "Active", tone: "success" as const, tip: null };
      case "past_due":
        return {
          label: "Past due",
          tone: "warning" as const,
          tip: `A payment failed or an invoice is overdue. Pay${
            sub.grace_until ? ` by ${formatDate(sub.grace_until)}` : ""
          } to keep everything running; after that the company is suspended (read-only).`,
        };
      case "suspended":
        return {
          label: "Suspended",
          tone: "danger" as const,
          tip: "Read-only for non-payment; DEs are paused. Paying the overdue invoice restores everything. Nothing is deleted.",
        };
      case "cancelled":
        return { label: "Cancelled", tone: "neutral" as const, tip: null };
    }
  })();
  return (
    <span className="inline-flex items-center gap-1" data-slot="subscription-status">
      <Badge variant={chip.tone}>{chip.label}</Badge>
      {chip.tip ? <InfoTip label="About the plan status">{chip.tip}</InfoTip> : null}
    </span>
  );
}

type IconType = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

/** An outbound hover action (Stripe's hosted page or PDF), opened in a new tab. */
export function RowLinkOut({
  icon,
  label,
  tooltip,
  href,
}: {
  icon: IconType;
  label: string;
  tooltip: string;
  href: string;
}) {
  return (
    <RowLinkAction
      icon={icon}
      label={label}
      tooltip={tooltip}
      render={<a href={href} target="_blank" rel="noopener noreferrer" />}
    />
  );
}

/** A section heading in the Plan area: 13 / 600, an optional (i) and actions on the right. */
export function SectionHead({
  id,
  title,
  info,
  children,
}: {
  id: string;
  title: string;
  info?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-8 items-center gap-2">
      <h2 id={id} className="text-sm font-semibold">
        {title}
      </h2>
      {info ? <InfoTip label={`About ${title}`}>{info}</InfoTip> : null}
      {children ? <div className="ml-auto flex items-center gap-1">{children}</div> : null}
    </div>
  );
}

/** "Oct 27 – Nov 27, 2026" for a period, else the date it was issued. */
export function invoiceWhen(inv: InvoiceView): string {
  if (inv.period_start && inv.period_end)
    return `${formatDate(inv.period_start)} – ${formatDate(inv.period_end)}`;
  return formatDate(inv.finalized_at ?? inv.created_at);
}
